import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { Button } from "@stackje/shared-components/ui/button";
import { Input } from "@stackje/shared-components/ui/input";
import { Label } from "@stackje/shared-components/ui/label";
import { useLogin } from "@/hooks/useLogin";
import type { AdminRouterContext } from "@/context/AdminRouterContext";
import { InstallPwaFullPage } from "@/components/InstallPwaFullPage";
import { OtpVerification } from "@/components/shared/OtpVerification";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Mail } from "lucide-react";

export const Route = createFileRoute("/auth/login")({
  validateSearch: (search: Record<string, unknown>) => ({
    returnTo: (search.returnTo as string) || undefined,
  }),
  beforeLoad: async ({
    context,
    search,
  }: {
    context: AdminRouterContext;
    search: { returnTo?: string };
  }) => {
    if (context.auth.session) {
      if (search.returnTo) {
        throw redirect({ href: search.returnTo, replace: true });
      }
      throw redirect({
        to: "/",
        replace: true,
      });
    }
    return {};
  },
  component: LoginComponent,
});

function LoginComponent() {
  const { returnTo } = Route.useSearch();
  const isOAuthFlow = returnTo?.includes("/oauth/");
  const {
    email,
    setEmail,
    otpCode,
    setOtpCode,
    formErrors,
    apiError,
    loading,
    handleLogin,
    showOtpInput,
    resendLoading,
    resendSuccess,
    handleResendVerification,
    handleOtpVerification,
  } = useLogin(returnTo);

  const content = (
    <AuthLayout oauthContext={isOAuthFlow}>
      <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 mb-1">
            {showOtpInput ? "Check your email" : "Welcome back"}
          </h1>
          <p className="text-sm text-gray-500 mb-8">
            {showOtpInput
              ? "Enter the code we sent you"
              : "Sign in with your email. No password, no payment."}
          </p>

          {apiError && (
            <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 mb-6">
              <p className="text-red-700 text-sm">{apiError}</p>
            </div>
          )}

          {showOtpInput ? (
            <OtpVerification
              email={email}
              otpCode={otpCode}
              setOtpCode={setOtpCode}
              loading={loading}
              onVerify={handleOtpVerification}
              onResend={handleResendVerification}
              resendLoading={resendLoading}
              message={
                resendSuccess
                  ? "Verification email sent! Please check your inbox."
                  : undefined
              }
            />
          ) : (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-gray-700">
                  Email address
                </Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="pl-10"
                    aria-invalid={formErrors?.email ? "true" : "false"}
                  />
                </div>
                {formErrors?.email && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {formErrors.email._errors.join(", ")}
                  </p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Sending code..." : "Continue with email"}
              </Button>
            </form>
          )}

          <p className="mt-8 text-center text-sm text-gray-500">
            Don&apos;t have an account?{" "}
            <Link
              to="/auth/signup"
              search={{ returnTo: returnTo }}
              className="font-medium text-orange-600 hover:text-orange-700"
            >
              Sign up
            </Link>
          </p>

          <p className="mt-3 text-center text-xs text-gray-400">
            We&apos;ll send a verification code to your email.
            <br />
            Same process whether you&apos;re signing in or signing up.
          </p>
      </div>
    </AuthLayout>
  );

  if (isOAuthFlow) {
    return content;
  }

  return <InstallPwaFullPage>{content}</InstallPwaFullPage>;
}
